import { Form, FormikProvider, useFormik } from 'formik';
import * as _ from 'lodash';
import moment from 'moment';
import QuillDelta from 'quill-delta';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';

import {
	useAuthenticatedUser,
	useCompleteWorkspaceCheckpoint,
	useLocalize,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	ActionListItem,
	GraphQLErrorMessage,
	Heading3,
	Modal,
	Paragraph,
	Popover,
	QuillInput,
	Separator,
	Spinner,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../../kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../../kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import validator from '../../../../kakapo-ui-services/factories/ValidatorFactory';
import LearningEventAttendee from '../components/LearningEventAttendee';
import LearningEventCreateHeaderForm from '../components/LearningEventCreateHeaderForm';
import LearningEventEditWarning from '../components/LearningEventEditWarning';
import LearningEventInviteAttendee, {
	IQueueJoinInput,
} from '../components/LearningEventInviteAttendee';
import useTimezoneSelectOptions from '../hooks/useTimezoneSelectOptions';

function LearningEventCancelConfirmationModal({
	onClose,
	learningEvent,
}: {
	learningEvent: graphql.LearningEvent;
	onClose: (confirmed?: boolean) => void;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [cancel] = graphql.useCancelLearningEventMutation();

	return (
		<Popover center visible>
			<Modal>
				<Modal.Header title={t('learning.event.cancel.modalTitle')} onClose={onClose} />
				<Modal.Contents>
					<Paragraph>
						{t('learning.event.cancel.description', { title: localize(learningEvent.title).text })}
					</Paragraph>
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={onClose}
					onSave={async () => {
						await cancel({ variables: { input: { id: learningEvent.id } } });

						onClose(true);
					}}
					saveTitle={t('learning.event.cancel.button')}
				/>
			</Modal>
		</Popover>
	);
}

export function LearningEventEditModal(props: {
	event?: graphql.LearningEvent;
	refetch?: () => void;

	/* If enabled and an event is passed, it will create a new event
	 * with the data from the passed event instead */
	copy?: boolean;
}) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();
	const history = useHistory();
	const authenticatedUser = useAuthenticatedUser();
	const timezoneOptions = useTimezoneSelectOptions();

	const { data: subjectsData, loading: subjectsLoading } = graphql.useLearningSubjectsQuery({
		variables: {
			first: 1000,
		},
	});

	const { data: typesData, loading: typesLoading } = graphql.useLearningTypesQuery({
		variables: {
			first: 1000,
		},
	});

	const { data: jobTitlesData, loading: jobTitlesLoading } = graphql.useJobTitlesQuery({
		variables: {
			first: 1000,
		},
	});

	const [queuedHosts, setQueuedHosts] = useState<IQueueJoinInput[]>([
		{
			id: authenticatedUser.id,
			firstName: authenticatedUser.firstName,
			lastName: authenticatedUser.lastName,
			attendeeType: graphql.LearningEventAttendeeType.Host,
		} as IQueueJoinInput,
	]);

	const queueJoin = (input: IQueueJoinInput) => {
		const existingIndex = queuedHosts.findIndex((host) => host.id === input.id);

		if (existingIndex !== -1) {
			const update = queuedHosts.slice();

			update[existingIndex] = {
				...update[existingIndex],
				attendeeType: input.attendeeType,
			};

			setQueuedHosts(update);

			return;
		}

		setQueuedHosts([...queuedHosts, input]);
	};

	const unqueueJoin = (id: string) => {
		const index = queuedHosts.findIndex((host) => host.id === id);

		if (index === -1) {
			return;
		}

		const newHosts = [...queuedHosts];

		newHosts.splice(index, 1);

		setQueuedHosts(newHosts);
	};

	const [join] = graphql.useJoinLearningEventMutation();

	const [create] = graphql.useCreateLearningEventMutation();
	const [update] = graphql.useUpdateLearningEventMutation();
	const [createAsset] = graphql.useCreateAssetMutation();
	const [changeCover] = graphql.useChangeLearningEventCoverMutation();

	const [coverFile, setCoverFile] = useState<File | undefined>(undefined);

	const [created, setCreated] = useState(false);

	const { addToast } = useToasts();

	const complete = useCompleteWorkspaceCheckpoint('workspace.learningEvents.create');

	const initialValues = {
		title: props.event?.title[0]?.text ?? '',
		introduction: props.event?.description?.[0]?.text ?? new QuillDelta(),
		date: props.event?.startDate ? new Date(props.event.startDate) : new Date(),
		language: props.event?.languageID ?? language.id,
		startTime: props.event?.startDate
			? moment(new Date(props.event.startDate)).format('HH:mm')
			: '09:00',
		endTime: props.event?.endDate ? moment(new Date(props.event.endDate)).format('HH:mm') : '11:00',
		location: props.event?.location ?? '',
		meetingLink: props.event?.meetingLink ?? '',
		maxAttendees: props.event?.capacity.toFixed() ?? '10',
		subjects: props.event?.learningSubjects.map((subject) => subject.id) ?? [],
		type: props.event?.learningType.id,
		jobTitles: props.event?.jobTitles?.map((jobTitle) => jobTitle.id) ?? [],
		timezone:
			props.event?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? undefined,
	};

	const pushEvent = (id: string) => {
		const { state } = history.location;
		const action = !props.copy ? history.push : history.replace;

		action({
			pathname: `/learning/event/${id}`,
			state: state,
		});
	};

	const formik = useFormik({
		validationSchema: validator.object({
			title: validator.string().min(1).required(),
			maxAttendees: validator.number().min(1).max(128).required(),
			startTime: validator
				.string()
				.required()
				.matches(/[0-9]{2,2}:[0-9]{2,2}/, {
					message: t('learning.event.modal.errors.invalidStartTime'),
				}),
			endTime: validator
				.string()
				.required()
				.matches(/[0-9]{2,2}:[0-9]{2,2}/, {
					message: t('learning.event.modal.errors.invalidEndTime'),
				}),
			language: validator.string().required(),
			type: validator.string().required(),
			subjects: validator.array(validator.string().required()),
			jobTitles: validator.array(validator.string().required()),
			location: validator.string().min(1),
			meetingLink: validator.string().url(),
		}),
		onSubmit: async (values) => {
			const startDate = new Date(values.date);
			const startTime = values.startTime.split(':').map((x) => parseInt(x));
			startDate.setHours(startTime[0]);
			startDate.setMinutes(startTime[1]);

			const endDate = new Date(values.date);
			const endTime = values.endTime.split(':').map((x) => parseInt(x));
			endDate.setHours(endTime[0]);
			endDate.setMinutes(endTime[1]);

			let id: string | undefined;

			if (props.event && !props.copy) {
				const response = await update({
					variables: {
						input: {
							id: props.event.id,
							title: [{ languageID: values.language, text: values.title }],
							capacity: parseInt(values.maxAttendees),
							startDate,
							endDate,
							languageID: values.language,
							learningSubjectIDs: values.subjects,
							learningTypeID: values.type!,
							jobTitleIDs: values.jobTitles,
							description: [{ languageID: values.language, text: values.introduction }],
							location: values.location || undefined,
							meetingLink: values.meetingLink || undefined,
							timezone: values.timezone || undefined,
						},
					},
				});

				id = response.data?.updateLearningEvent.learningEvent.id;
			} else {
				if (created) {
					return;
				}

				setCreated(true);

				const response = await create({
					variables: {
						input: {
							title: [{ languageID: values.language, text: values.title }],
							capacity: parseInt(values.maxAttendees),
							startDate,
							endDate,
							languageID: values.language,
							learningSubjectIDs: values.subjects,
							learningTypeID: values.type!,
							jobTitleIDs: values.jobTitles,
							description: [{ languageID: values.language, text: values.introduction }],
							location: values.location || undefined,
							meetingLink: values.meetingLink || undefined,
							timezone: values.timezone || undefined,
						},
					},
				});

				complete();

				id = response.data?.createLearningEvent.learningEvent.id;

				for (const host of queuedHosts) {
					if (host.id! === authenticatedUser.id) {
						continue;
					}

					await join({
						variables: {
							input: {
								learningEventID: id!,
								userID: host.id!,
								attendeeType: host.attendeeType,
							},
						},
					});

					setQueuedHosts([]);
				}
			}

			if (!id) {
				return;
			}

			if (props.copy) {
				if (props.event?.cover) {
					await changeCover({
						variables: {
							input: {
								id,
								assetID: props.event?.cover?.id,
							},
						},
					});
				}
			} else {
				if (coverFile) {
					try {
						const { data: createAssetData } = await createAsset({
							variables: {
								input: {
									contentLength: coverFile.size,
									contentType: coverFile.type,
								},
							},
						});

						if (!createAssetData) {
							return;
						}

						const { asset, uploadEndpoint } = createAssetData.createAsset;

						const headers = new Headers();

						for (const { name, values } of uploadEndpoint.headers) {
							for (const value of values) {
								headers.append(name, value);
							}
						}

						await fetch(uploadEndpoint.url, {
							method: 'PUT',
							headers,
							body: coverFile,
						});

						await changeCover({
							variables: {
								input: {
									id,
									assetID: asset.id,
								},
							},
						});
					} catch (_) {
						addToast(t('learning.event.modal.errors.coverUploadFailed'), {
							appearance: 'error',
							autoDismiss: true,
						});
					}
				}
			}

			props.refetch?.();

			pushEvent(id);
		},
		initialValues,
	});

	const keys = [
		'title',
		'introduction',
		'date',
		'startTime',
		'endTime',
		'maxAttendees',
		'meetingLink',
		'location',
	];

	const showWarning =
		!_.isMatch(_.pick(initialValues, keys), _.pick(formik.values, keys)) &&
		props.event &&
		!props.copy;

	const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

	if (props.event?.status === graphql.LearningEventStatus.Cancelled) {
		pushEvent(props.event!.id);
	}

	return (
		<>
			<Popover center visible>
				<Modal>
					<Modal.Header
						title={t(
							`learning.event.modal.${props.event && !props.copy ? 'edit' : 'create'}.title`,
						)}
						onClose={() => history.goBack()}
					/>
					<Modal.Contents>
						{showWarning && <LearningEventEditWarning />}
						<FormikProvider value={formik}>
							<Form>
								<LearningEventCreateHeaderForm
									event={props.event}
									coverFile={coverFile}
									setCoverFile={setCoverFile}
								/>

								<Box mb={3}>
									<GraphQLErrorMessage
										error={
											formik.isValid || formik.submitCount === 0
												? null
												: ({
														extensions: {
															error: {
																key: 'common.errors.oneOrMoreValidationErrorsOccured',
																data: {},
															},
														},
													} as any)
										}
									/>
								</Box>

								<Box mb={2}>
									<Heading3>{t('learning.event.modal.introduction.title')}</Heading3>
								</Box>

								<Box mb={3}>
									<Separator />
								</Box>

								<Box mb={4}>
									<QuillInput.Formik name="introduction" />
								</Box>

								<Box my={2}>
									<Heading3>{t('learning.event.modal.hosts.title')}</Heading3>
								</Box>

								<Separator />

								<Box mb={4}>
									{props.event
										? props.event.hosts.edges.map((host) => (
												<LearningEventAttendee
													key={host.node.id}
													user={host.node}
													type={host.attendeeType}
													event={props.event}
												/>
											))
										: queuedHosts.map((host) => (
												<LearningEventAttendee
													key={host.id}
													user={host}
													type={host.attendeeType}
													event={props.event}
													queueJoin={queueJoin}
													unqueueJoin={unqueueJoin}
												/>
											))}
									<Box mt={3}>
										<LearningEventInviteAttendee
											event={props.event}
											attendeeType={graphql.LearningEventAttendeeType.Host}
											queueJoin={queueJoin}
											queuedHosts={queuedHosts}
										/>
									</Box>
								</Box>

								<Box my={2}>
									<Heading3>{t('learning.event.modal.details.title')}</Heading3>
								</Box>

								<Box mb={3}>
									<Separator />
								</Box>

								<Box mb={4}>
									<DetailsTableForm
										formValues={[
											{
												label: t('learning.event.modal.details.startTime.label'),
												name: 'startTime',
												type: DetailsTableFormValueTypes.Text,
											},
											{
												label: t('learning.event.modal.details.endTime.label'),
												name: 'endTime',
												type: DetailsTableFormValueTypes.Text,
											},
											{
												label: t('learning.event.modal.details.location.label'),
												name: 'location',
												type: DetailsTableFormValueTypes.Text,
											},
											{
												label: t('learning.event.modal.details.meetingLink.label'),
												name: 'meetingLink',
												type: DetailsTableFormValueTypes.Text,
											},
											{
												label: t('learning.event.modal.details.maxAttendees.label'),
												name: 'maxAttendees',
												type: DetailsTableFormValueTypes.Text,
											},
											{
												label: t('learning.event.modal.details.timezone.label'),
												name: 'timezone',
												type: DetailsTableFormValueTypes.Select,
												search: true,
												options: timezoneOptions,
											},
										]}
									/>
								</Box>

								<Box my={2}>
									<Heading3>{t('learning.event.modal.meta.title')}</Heading3>
								</Box>

								<Box mb={3}>
									<Separator />
								</Box>

								<Box mb={4}>
									{subjectsLoading || typesLoading || jobTitlesLoading ? (
										<Spinner centered />
									) : (
										<DetailsTableForm
											formValues={[
												{
													label: t('learning.event.modal.meta.type.label'),
													name: 'type',
													type: DetailsTableFormValueTypes.Select,
													options: typesData?.learningTypes.edges.map(({ node }) => ({
														value: node.id,
														label: localize(node.title)?.text,
													})),
												},
												{
													label: t('learning.event.modal.meta.subjects.label'),
													name: 'subjects',
													type: DetailsTableFormValueTypes.MultiSelect,
													options: subjectsData?.learningSubjects.edges.map(({ node }) => ({
														value: node.id,
														label: localize(node.title)?.text,
													})),
												},
												{
													label: t('learning.event.modal.meta.language.label'),
													name: 'language',
													type: DetailsTableFormValueTypes.Select,
													options: [
														{ value: 'nl_NL', label: t('common.language.nl_NL') },
														{ value: 'en_US', label: t('common.language.en_US') },
													],
												},
												{
													label: t('learning.event.modal.meta.jobTitles.label'),
													name: 'jobTitles',
													type: DetailsTableFormValueTypes.MultiSelect,
													options: jobTitlesData?.jobTitles.edges.map(({ node }) => ({
														value: node.id,
														label: localize(node.title)?.text,
													})),
												},
											]}
										/>
									)}
								</Box>

								{props.event && (
									<>
										<Box my={2}>
											<Heading3>{t('learning.event.modal.actions.title')}</Heading3>
										</Box>

										<Box mb={3}>
											<Separator />
										</Box>

										<Box mt={4}>
											<ActionArchiveListItem
												archivableItem={props.event}
												archivableItemName="event"
												archivableItemKey="event"
												useArchive={graphql.useArchiveLearningEventMutation as any}
												useRestore={graphql.useRestoreLearningEventMutation as any}
											/>

											<ActionListItem
												buttonTitle={t('learning.event.modal.actions.cancel.button')}
												label={t('learning.event.modal.actions.cancel.label')}
												onButtonClick={(event) => {
													setShowCancelModal(true);
													event.preventDefault();
													event.stopPropagation();
												}}
												buttonIndication="destructive"
											/>
										</Box>
									</>
								)}
							</Form>
						</FormikProvider>
					</Modal.Contents>
					<Modal.FormFooter
						onCancel={() => history.goBack()}
						onSave={formik.submitForm}
						saveTitle={t('learning.event.modal.save')}
					/>
				</Modal>
			</Popover>
			{props.event && showCancelModal && (
				<LearningEventCancelConfirmationModal
					learningEvent={props.event}
					onClose={(confirmed) => {
						setShowCancelModal(false);

						if (confirmed) {
							pushEvent(props.event!.id);
						}
					}}
				/>
			)}
		</>
	);
}

export default function LearningEventEditModalID(props: { eventID: string; copy?: boolean }) {
	const { data } = graphql.useLearningEventQuery({
		variables: {
			id: props.eventID,
		},
	});

	if (data) {
		return (
			<LearningEventEditModal
				event={data.learningEvent as graphql.LearningEvent}
				copy={props.copy}
			/>
		);
	}

	return (
		<Popover visible center>
			<Modal>
				<Spinner centered />
			</Modal>
		</Popover>
	);
}
